import type { VesselMessage } from '../../types/VesselMessage';
import { Button } from '../cdl/Button/Button';
import { useDrawer } from '../cdl/Drawer/useDrawer.hook';
import { IconButton } from '../cdl/IconButton/IconButton';
import { Label } from '../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../cdl/RadioGroup/RadioGroup';
import { Text } from '../cdl/Text/Text';
import { ButtonGroup } from '../common/buttons/ButtonGroup';
import { HeaderWithLine } from '../common/HeaderWithLine/HeaderWithLine';
import { formatVessel } from '../common/helpers/formatVessel.helper';
import { translate } from '../common/helpers/translate.helper';
import { useUpdateEffect } from '../common/hooks/useUpdateEffect';
import { IconPencil } from '../common/icons/cdl/Pencil';
import { IconPlus } from '../common/icons/cdl/Plus';
import { IconTrash } from '../common/icons/cdl/Trash';
import { Box } from '../common/ui/Box';
import { theme } from '../common/ui/theme';

import { KeyPortVesselScope } from './types/KeyPortVesselScope';
import type { VesselLimitationSubmitArgs } from './VesselLimitationForm';
import { VesselLimitationForm } from './VesselLimitationForm';

interface FormVesselLimitationsSectionProps {
    vessels: VesselMessage[];
    disabled: boolean;
    onSubmit: (vessels: VesselMessage[]) => void;
    groupId: string | null;
    scope: KeyPortVesselScope;
    onScopeChange: (scope: KeyPortVesselScope) => void;
}

export const FormVesselLimitationsSection = ({
    vessels,
    disabled,
    onSubmit,
    groupId,
    scope,
    onScopeChange,
}: FormVesselLimitationsSectionProps) => {
    const { push, pop } = useDrawer();

    useUpdateEffect(() => onSubmit([]), [groupId]);

    const alreadyExists = (newVessel: VesselMessage) => vessels.some((vessel) => vessel.id === newVessel.id);

    const onVesselLimitationAddSubmit = ({ data: newVessel }: VesselLimitationSubmitArgs) => {
        if (!alreadyExists(newVessel)) {
            onSubmit([...vessels, newVessel]);
        }
    };

    const onVesselLimitationEditSubmit = ({ data: newVessel, oldVessel }: VesselLimitationSubmitArgs) => {
        if (!oldVessel || alreadyExists(newVessel)) {
            return;
        }

        onSubmit(vessels.map((vessel) => (vessel.id === oldVessel.id ? newVessel : vessel)));
    };

    const openVesselLimitationModal = (vessel?: VesselMessage) => {
        const drawerId = push({
            header: {
                title: vessel ? translate('keyPorts.editVesselLimitation') : translate('keyPorts.addVesselLimitation'),
            },
            width: '15vw',
            offsetParent: false,
            content: (
                <VesselLimitationForm
                    primaryActionText={translate(
                        vessel ? 'keyPorts.updateVesselLimitation' : 'keyPorts.addVesselLimitation'
                    )}
                    onSubmit={(args) => {
                        if (vessel) {
                            onVesselLimitationEditSubmit(args);
                        } else {
                            onVesselLimitationAddSubmit(args);
                        }
                        pop(drawerId);
                    }}
                    data={vessel ? { vessel } : undefined}
                    groupId={groupId}
                />
            ),
        });
    };

    const deleteVesselLimitation = (vesselToDelete: VesselMessage) => {
        onSubmit(vessels.filter((vessel) => vessel.id !== vesselToDelete.id));
    };

    return (
        <Box marginTop={16}>
            <HeaderWithLine>
                <Text variant="subtitle1" as="h2">
                    {translate('keyPorts.vesselLimitation.headline')}
                </Text>
            </HeaderWithLine>

            <RadioGroup
                style={{ marginTop: theme.space[16] }}
                disabled={disabled}
                value={scope}
                onValueChange={(value) =>
                    onScopeChange(
                        value === KeyPortVesselScope.ALL ? KeyPortVesselScope.ALL : KeyPortVesselScope.SELECTED
                    )
                }
                aria-label={translate('keyPorts.vesselLimitation.headline')}
            >
                <RadioGroupItem>
                    <Radio value={KeyPortVesselScope.ALL} />
                    <Label>{translate('keyPorts.allVessels')}</Label>
                </RadioGroupItem>
                <RadioGroupItem>
                    <Radio value={KeyPortVesselScope.SELECTED} />
                    <Label>{translate('keyPorts.selectedVesselsOnly')}</Label>
                </RadioGroupItem>
            </RadioGroup>

            {scope === KeyPortVesselScope.SELECTED ? (
                <Box display="flex" flexDirection="column" alignItems="start" rowGap={16} marginTop={16}>
                    <Text>{translate('keyPorts.vesselLimitation.text')}</Text>

                    {vessels.map((vessel) => (
                        <Box
                            key={vessel.id}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            marginBottom={6}
                            width="100%"
                        >
                            <Text>{formatVessel({ vessel })}</Text>

                            <ButtonGroup>
                                <IconButton
                                    disabled={disabled}
                                    Icon={IconPencil}
                                    type="button"
                                    onClick={() => openVesselLimitationModal(vessel)}
                                    tooltipLabel={translate('keyPorts.editVesselLimitation')}
                                />
                                <IconButton
                                    disabled={disabled}
                                    Icon={IconTrash}
                                    destructive
                                    type="button"
                                    onClick={() => deleteVesselLimitation(vessel)}
                                />
                            </ButtonGroup>
                        </Box>
                    ))}

                    <Button
                        type="button"
                        onClick={() => openVesselLimitationModal()}
                        disabled={disabled || !groupId}
                        LeadingIcon={IconPlus}
                    >
                        {translate('keyPorts.addVesselLimitation')}
                    </Button>
                </Box>
            ) : null}
        </Box>
    );
};
